import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { dispatchAPI } from '../../services/api';
import DateRangeFilter, { filterByDateRange } from './DateRangeFilter';

const COLORS = ['#24549c', '#3a6fc4', '#FF9800', '#4CAF50', '#E65100', '#6A1B9A', '#1a3d73'];
const statusColors = { 'Packed': '#3a6fc4', 'In Transit': '#FF9800', 'Delivered': '#4CAF50', 'Returned': '#E65100' };

const DispatchReport = () => {
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('customer');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  useEffect(() => {
    const fetch = async () => {
      try {
        const d = await dispatchAPI.getAll();
        setDispatches(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const filteredDispatches = useMemo(() => filterByDateRange(dispatches, dateRange), [dispatches, dateRange]);
  const total = filteredDispatches.length;
  const delivered = filteredDispatches.filter(d => d.status === 'Delivered').length;
  const inTransit = filteredDispatches.filter(d => d.status === 'In Transit').length;
  const packed = filteredDispatches.filter(d => d.status === 'Packed').length;
  const deliveryRate = total > 0 ? ((delivered / total) * 100).toFixed(0) : 0;

  // Customer-wise
  const customerData = useMemo(() => {
    const map = {};
    filteredDispatches.forEach(d => {
      const name = d.customer_name || 'Unknown';
      if (!map[name]) map[name] = { name, total: 0, delivered: 0, inTransit: 0, packed: 0 };
      map[name].total++;
      if (d.status === 'Delivered') map[name].delivered++;
      else if (d.status === 'In Transit') map[name].inTransit++;
      else if (d.status === 'Packed') map[name].packed++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredDispatches]);

  // Status-wise
  const statusData = useMemo(() => {
    const map = {};
    filteredDispatches.forEach(d => {
      if (!map[d.status]) map[d.status] = { name: d.status, count: 0 };
      map[d.status].count++;
    });
    return Object.values(map);
  }, [filteredDispatches]);

  // Vehicle-wise
  const vehicleData = useMemo(() => {
    const map = {};
    filteredDispatches.forEach(d => {
      const v = d.vehicle_number || 'Not Assigned';
      if (!map[v]) map[v] = { name: v, total: 0, driver: d.driver_name || '-' };
      map[v].total++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredDispatches]);

  const currentData = view === 'customer' ? customerData : view === 'status' ? statusData : vehicleData;

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(r => Object.values(r).join(','));
    const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${filename}.csv`; a.click();
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Dispatch Analytics</h2>
          <p className="text-xs text-gray-500">Customer-wise, status & vehicle breakdown of shipments</p>
        </div>
        <button onClick={() => exportCSV(currentData, `dispatch-${view}`)} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}><Download className="w-3.5 h-3.5" /> Export CSV</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
        {[
          { l: 'Total Dispatches', v: total },
          { l: 'Delivered', v: delivered },
          { l: 'In Transit', v: inTransit },
          { l: 'Packed / Ready', v: packed },
          { l: 'Delivery Rate', v: `${deliveryRate}%` },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{s.v}</p>
            <p className="text-[10px] text-gray-500">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <DateRangeFilter onChange={setDateRange} />
        <div className="flex gap-1.5 ml-auto">
          {[{ id: 'customer', label: 'Customer-wise' }, { id: 'status', label: 'Status-wise' }, { id: 'vehicle', label: 'Vehicle-wise' }].map(t => (
            <button key={t.id} onClick={() => setView(t.id)} className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${view === t.id ? 'text-white' : 'text-gray-600 bg-gray-100'}`} style={view === t.id ? { backgroundColor: '#24549c' } : {}}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">{view === 'customer' ? 'Customer' : view === 'status' ? 'Status' : 'Vehicle'} Breakdown</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={currentData.slice(0, 8)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9CA3AF' }} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
              {view === 'customer' ? (
                <><Legend wrapperStyle={{ fontSize: '10px' }} /><Bar dataKey="delivered" name="Delivered" fill="#4CAF50" radius={[3, 3, 0, 0]} /><Bar dataKey="inTransit" name="In Transit" fill="#FF9800" radius={[3, 3, 0, 0]} /><Bar dataKey="packed" name="Packed" fill="#3a6fc4" radius={[3, 3, 0, 0]} /></>
              ) : (
                <Bar dataKey={view === 'status' ? 'count' : 'total'} fill="#24549c" radius={[3, 3, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={currentData.filter(d => (d.total || d.count) > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey={view === 'status' ? 'count' : 'total'} nameKey="name">
                {currentData.map((d, i) => <Cell key={i} fill={statusColors[d.name] || COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">
            {currentData.filter(d => (d.total || d.count) > 0).map((d, i) => (
              <span key={i} className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors[d.name] || COLORS[i % COLORS.length] }} />{d.name}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-100 bg-gray-50">
            {['Name', ...(view === 'customer' ? ['Total', 'Delivered', 'In Transit'] : view === 'vehicle' ? ['Trips', 'Driver'] : ['Count'])].map(h => (
              <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {currentData.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-gray-400">No dispatches found</td></tr>
            ) : (
              currentData.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-semibold text-gray-900">{r.name}</td>
                  {view === 'customer' && <><td className="px-4 py-3 text-gray-500">{r.total}</td><td className="px-4 py-3 text-green-600">{r.delivered}</td><td className="px-4 py-3 text-amber-600">{r.inTransit}</td></>}
                  {view === 'vehicle' && <><td className="px-4 py-3 text-gray-500">{r.total}</td><td className="px-4 py-3 text-gray-500">{r.driver}</td></>}
                  {view === 'status' && <td className="px-4 py-3 text-gray-500">{r.count}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DispatchReport;
